import { RefObject, useCallback, useEffect, useState } from 'react';

interface UseInfinityScrollOptions {
  threshold?: number;
  rootMargin?: string;
}

export const useInfinityScroll = <T>(
  ref: RefObject<HTMLElement | null>,
  fetch: (page: number) => Promise<T[]>,
  options: UseInfinityScrollOptions = {}
) => {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { threshold = 0.0, rootMargin = '0px' } = options;

  const scrollObserver = useCallback(
    () =>
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !loading && hasMore) {
              setLoading(true);
              setError(null);
              
              fetch(page)
                .then((newData) => {
                  if (newData.length === 0) {
                    setHasMore(false);
                  } else {
                    setData((oldValue) => [...oldValue, ...newData]);
                    setPage((prevPage) => prevPage + 1);
                  }
                })
                .catch((err) => {
                  setError(err instanceof Error ? err : new Error('Failed to fetch data'));
                })
                .finally(() => {
                  setLoading(false);
                });
            }
          });
        },
        { threshold, rootMargin }
      ),
    [page, fetch, loading, hasMore, threshold, rootMargin]
  );

  useEffect(() => {
    const target = ref.current;
    if (target && hasMore) {
      const observer = scrollObserver();
      observer.observe(target);
      return () => {
        observer.unobserve(target);
      };
    }
  }, [scrollObserver, ref, hasMore]);

  const initializeData = useCallback((initialData: T[]) => {
    setData(initialData);
    setPage(2); // 初期データは1ページ目なので、次は2ページ目から
    setHasMore(true);
    setLoading(false);
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
    setLoading(false);
    setError(null);
  }, []);

  return {
    data,
    loading,
    hasMore,
    error,
    initializeData,
    reset
  };
};
